/**
 * Storage Helpers for Dashboard Layout
 * Handles API storage with fallback to local storage
 */

import { DashboardLayout } from "../types";
import { validateLayout, generateDefaultLayout, generateInstanceId } from "./defaultLayout";
import XanoAPI from "../../../lib/XanoAPI";

const STORAGE_KEY = "dashboard-v2-layout";

/**
 * Loads layout from local storage (synchronous, for initial render)
 */
export function loadLayoutFromStorage(): DashboardLayout | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    
    const parsed = JSON.parse(stored) as DashboardLayout;
    // validateLayout preserves array order (uses .map() which maintains order)
    // Widget order is stored by array index - position in array = display order
    const validated = validateLayout(parsed);
    console.log("Layout loaded from localStorage. Widget order:", validated.widgets.map(w => w.widgetId));
    return validated;
  } catch (error) {
    console.error("Error loading layout from storage:", error);
    return null;
  }
}

/**
 * Loads layout from API (with localStorage fallback)
 * This is async and should be called after initial render
 */
export async function loadLayoutFromAPI(): Promise<DashboardLayout | null> {
  try {
    const api = XanoAPI.getInstance();
    const userId = api.getUserId();
    
    // Load from database only
    if (userId && userId > 0) {
      try {
        const apiLayout = await api.getUserDashboards(userId);
        console.log("loadLayoutFromAPI: Raw API data type:", typeof apiLayout);
        console.log("loadLayoutFromAPI: Raw API data:", apiLayout);
        
        if (apiLayout) {
          // Check if apiLayout is already a DashboardLayout or if it needs parsing
          let layoutToValidate: any = apiLayout;
          
          // If apiLayout has a widgets property, use it directly
          if (apiLayout.widgets && Array.isArray(apiLayout.widgets)) {
            layoutToValidate = apiLayout;
            
            // CRITICAL: Log the widget order BEFORE any processing
            const rawWidgetOrder = layoutToValidate.widgets.map((w: any) => w.widgetId).join(',');
            const rawHiddenWidgets = layoutToValidate.widgets.filter((w: any) => w.visible === false).map((w: any) => w.widgetId);
            console.log("loadLayoutFromAPI: Raw widget order from API:", rawWidgetOrder);
            console.log("loadLayoutFromAPI: Raw hidden widgets from API:", rawHiddenWidgets.length > 0 ? rawHiddenWidgets.join(',') : 'none');
            console.log("loadLayoutFromAPI: Raw column layout (cols) from API:", JSON.stringify(layoutToValidate.cols));
            console.log("loadLayoutFromAPI: Widget count:", layoutToValidate.widgets.length);
          } else if (typeof apiLayout === 'object') {
            // It might be wrapped or have a different structure
            layoutToValidate = apiLayout;
          }
          
          // Validate and fix layout structure (preserves order)
          const validated = validateLayout(layoutToValidate);
          const validatedWidgetOrder = validated.widgets.map(w => w.widgetId).join(',');
          const validatedHiddenWidgets = validated.widgets.filter(w => !w.visible).map(w => w.widgetId);
          console.log("loadLayoutFromAPI: Validated widget order:", validatedWidgetOrder);
          console.log("loadLayoutFromAPI: Validated hidden widgets:", validatedHiddenWidgets.length > 0 ? validatedHiddenWidgets.join(',') : 'none');
          console.log("loadLayoutFromAPI: Validated column layout (cols):", JSON.stringify(validated.cols));
          
          // Verify order was preserved
          if (layoutToValidate.widgets && Array.isArray(layoutToValidate.widgets)) {
            const orderMatches = validated.widgets.length === layoutToValidate.widgets.length &&
              validated.widgets.every((w, i) => w.widgetId === layoutToValidate.widgets[i]?.widgetId);
            if (!orderMatches) {
              console.warn("loadLayoutFromAPI: WARNING - Widget order may have changed during validation!");
            }
          }
          
          return validated;
        } else {
          console.log("loadLayoutFromAPI: No API layout data found in database");
        }
      } catch (apiError) {
        console.error("Failed to load layout from API:", apiError);
        return null;
      }
    }
    
    // No user authenticated or API load failed
    return null;
  } catch (error) {
    console.error("Error loading layout from API:", error);
    return loadLayoutFromStorage();
  }
}

/**
 * Saves layout to database (API only)
 * Stores:
 * - Widget order (by array index in widgets array)
 * - Widget visibility (visible property on each widget)
 * - Column layout (cols object)
 */
export async function saveLayoutToStorage(
  layout: DashboardLayout,
  options?: { skipPusherBroadcast?: boolean }
): Promise<{ success: boolean; error?: string }> {
  try {
    // CRITICAL: Preserve widget order exactly as it appears in the array
    // The order of widgets in the array determines display order
    // This order MUST be restored exactly on page reload
    
    // Create a deep copy to ensure we're saving the exact current state
    const layoutToSave: DashboardLayout = {
      ...layout,
      // CRITICAL: Use slice() to create a new array with exact same order
      // Do NOT use map() or any operation that could reorder
      widgets: layout.widgets.slice(), // Preserve exact array order and create new array reference
      cols: { ...layout.cols },
    };
    
    // Log the order being saved for debugging
    const widgetOrderString = layoutToSave.widgets.map(w => w.widgetId).join(',');
    const hiddenWidgets = layoutToSave.widgets.filter(w => !w.visible).map(w => w.widgetId);
    console.log("saveLayoutToStorage: Saving widget order to database:", widgetOrderString);
    console.log("saveLayoutToStorage: Hidden widgets:", hiddenWidgets.length > 0 ? hiddenWidgets.join(',') : 'none');
    console.log("saveLayoutToStorage: Column layout (cols):", JSON.stringify(layoutToSave.cols));
    
    const api = XanoAPI.getInstance();
    const userId = api.getUserId();
    
    // Save to database (API)
    if (!userId || userId <= 0) {
      return { 
        success: false, 
        error: "User not authenticated. Cannot save to database." 
      };
    }
    
    try {
      // Stringify with explicit order preservation
      const jsonString = JSON.stringify(layoutToSave);
      console.log("saveLayoutToStorage: JSON string length:", jsonString.length);
      
      // Save to database
      console.log("saveLayoutToStorage: Calling API to save layout...");
      console.log("saveLayoutToStorage: User ID:", userId);
      const response = await api.saveUserDashboards(userId, layoutToSave);
      console.log("saveLayoutToStorage: Database response:", response);
      console.log("saveLayoutToStorage: Response type:", typeof response);
      
      // Check if response indicates success
      // Some APIs return the saved object, others return a success message
      const responseIsValid = response && (
        (typeof response === 'string' && response.length > 0) ||
        (typeof response === 'object' && response !== null && (
          (response as any).widgets || 
          (response as any).success !== false
        ))
      );
      
      if (!responseIsValid) {
        console.warn("saveLayoutToStorage: Response may not indicate success:", response);
      }
      
      console.log("Layout saved to database. Widget order:", widgetOrderString);
      
      // Log detailed widget visibility states for debugging
      const visibilityMap = layoutToSave.widgets.map(w => `${w.widgetId}:${w.visible ? 'visible' : 'hidden'}`).join(', ');
      console.log("saveLayoutToStorage: Widget visibility states:", visibilityMap);
      
      // Note: We return success because the API call succeeded (200 response)
      // However, the backend may not be persisting to user.dashboards field
      // The verification logging in XanoAPI will show if data was actually saved
      // If verification fails, the backend endpoint needs to be configured to save to database
      return { success: true };
    } catch (apiError: any) {
      const errorMessage = apiError?.message || apiError?.toString() || "Unknown error";
      console.error("Failed to save layout to database:", apiError);
      console.error("Error details:", {
        message: errorMessage,
        status: apiError?.status,
        url: apiError?.url,
        response: apiError?.response,
      });
      
      return { 
        success: false, 
        error: `Database save failed: ${errorMessage}` 
      };
    }
  } catch (error: any) {
    const errorMessage = error?.message || error?.toString() || "Unknown error";
    console.error("Error saving layout to database:", error);
    return { success: false, error: errorMessage };
  }
}

/**
 * Clears layout from local storage
 */
export function clearLayoutFromStorage(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error("Error clearing layout from storage:", error);
  }
}

/**
 * Gets default layout (for first-time users)
 */
export function getDefaultLayout(): DashboardLayout {
  return generateDefaultLayout();
}

